<template>
  <div
    class="tz-location-search-filter"
    :class="toggleShow ? 'tz-location-search-filter--show' : null"
  >
    <strong
      v-for="item in renderIf(!isMobile)"
      :key="item"
      class="tz-location-search-filter__title"
      :class="{ 'tz-location-search-filter__title--open': canCollapse }"
      v-text="title"
      @click="canCollapse ? handleClick($event) : ''"
    />
    <ul
      v-for="item in renderIf(!isMobile)"
      :key="item"
      class="tz-location-search-filter__list"
      ref="filterList"
    >
      <li
        :key="index + now"
        class="tz-location-search-filter__item"
        v-for="(location, index) in displayLocationList"
      >
        <a
          class="tz-location-search-filter__link"
          :href="getLocationLink(location)"
          :rel="
            (nofollowLimit && location.count <= nofollowLimit) ||
            (location.count && nofollow)
              ? 'nofollow'
              : null
          "
        >
          <span class="tz-location-search-filter__name">{{
            location.displayName
          }}</span
          ><span
            v-for="item in renderIf(location.count)"
            :key="item"
            class="tz-location-search-filter__count"
            >{{ formatCount(location) }}</span
          >
        </a>
      </li>
    </ul>
    <button
      v-for="item in renderIf(
        !canCollapse && shouldShowMore && isShowingMore && !hideShowMoreButton
      )"
      :key="item"
      class="tz-location-search-filter__button tz-location-search-filter__button--less"
      role="button"
      aria-label="Show Less Results"
      @click="toggleMoreResults"
      v-text="showLessText"
    />
    <button
      v-for="item in renderIf(
        !canCollapse && shouldShowMore && !isShowingMore && !hideShowMoreButton
      )"
      :key="item"
      class="tz-location-search-filter__button tz-location-search-filter__button--more"
      role="button"
      aria-label="Show More Results"
      @click="toggleMoreResults"
      v-text="showMoreText"
    />
    <select
      v-for="item in renderIf(isMobile)"
      :key="item"
      :aria-label="title"
      class="tz-location-search-filter__select"
      v-model="selectedFilter"
      @change="filterResults"
    >
      <option selected disabled :value="null">{{ title }}</option>
      <option
        :key="index + now"
        class="tz-location-search-filter__option"
        v-for="(location, index) in locationList"
        :value="getLocationLink(location)"
        :rel="
          (nofollowLimit && location.count <= nofollowLimit) ||
          (location.count && nofollow)
            ? 'nofollow'
            : null
        "
        >{{ location.displayName }}{{ formatCount(location) }}</option
      >
    </select>
  </div>
</template>

<script>
export default {
  name: 'TzLocationSearchFilter',
  computed: {
    displayLocationList: function() {
      return this.locationList;
    },
    shouldShowMore: function() {
      return !this.isMobile;
    }
  },
  data() {
    return {
      isShowingMore: false,
      toggleShow: false,
      showMobile: false,
      now: Date.now(),
      selectedFilter: this.preselectedLocation
    };
  },
  props: {
    // Base URL we will be adding location to formatted without trailing slash
    baseUrl: {
      type: String,
      required: true
    },
    // Optional prefixing sybmol to count
    countPrefix: {
      type: String,
      default: ''
    },
    // Add a space between the count prefix and the preceding location name
    countPrefixSpace: {
      type: Boolean,
      default: true
    },
    // Optional suffixing symbol to count
    countSuffix: {
      type: String,
      default: ''
    },
    // Return true if mobile style should be taken into account
    isMobile: {
      type: Boolean,
      default: false
    },
    // List of locations in format of [{displayName: string:required, urlValue: string:optional, count: number:optional}]
    locationList: {
      type: Array,
      required: true
    },
    // Any characters that should be prepended to the location name when added to url
    locationPrepend: {
      type: String,
      default: ''
    },
    // The urlValue of the selected location to be preselected on small screens
    preselectedLocation: {
      type: String,
      default: null
    },
    // String to display on more button when all results displayed
    showLessText: {
      type: String,
      default: 'Show Less'
    },
    // String to display on  more button when limited results displayed
    showMoreText: {
      type: String,
      default: 'Show More'
    },
    // Title to be displayed on component
    title: {
      type: String,
      required: true
    },
    // Records Count Upper Limit at which to show nofollow attribute
    nofollowLimit: {
      type: Number
    },
    // Add nofollow attribute to all links
    nofollow: {
      type: Boolean,
      default: false
    },
    // pass as true to remove the trailing slash from the location links
    removeTrailingSlash: {
      type: Boolean,
      default: false
    },
    // pass true to override internal logic to hide the show more/less button from a parent component
    hideShowMoreButton: {
      type: Boolean,
      default: false
    },
    // Collapse list when clicking on the title
    canCollapse: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    filterResults: function() {
      window.location.href = this.selectedFilter;
    },
    formatCount: function(location) {
      if (!location.count) {
        return null;
      }

      return `${this.countPrefixSpace ? ' ' : ''}${this.countPrefix}${
        location.count
      }${this.countSuffix}`;
    },
    getLocationLink: function(location) {
      return `${this.baseUrl}${this.removeTrailingSlash ? '' : '/'}${
        this.locationPrepend
      }${location.urlValue ? location.urlValue : ''}`;
    },
    /**
     * If statement vue hack to prevent <!-- --> comment
     * @param condition
     * @returns {[]}
     */
    renderIf(condition) {
      return condition ? [Math.random()] : [];
    },
    toggleMoreResults: function() {
      this.isShowingMore = !this.isShowingMore;
      this.toggleShow = !this.toggleShow;
    },
    expandList: function() {
      const list = this.$refs.filterList[0];
      const height = this.$refs.filterList[0].scrollHeight;

      // have the element transition to the height of its inner content
      list.style.height = height + 'px';

      // when the next css transition finishes (which should be the one we just triggered)
      list.addEventListener(
        'transitionend',
        function() {
          // remove "height" from the element's inline styles, so it can return to its initial value
          list.style.height = null;
        },
        { once: true }
      );

      // mark the section as "currently not collapsed"
      list.setAttribute('data-collapsed', 'false');
    },
    collapseList: function() {
      const list = this.$refs.filterList[0];
      const height = this.$refs.filterList[0].scrollHeight;
      const elementTransition = list.style.transition;
      list.style.transition = '';
      requestAnimationFrame(function() {
        list.style.height = height + 'px';
        list.style.transition = elementTransition;

        requestAnimationFrame(function() {
          list.style.height = 0 + 'px';
        });
      });
      list.setAttribute('data-collapsed', 'true');
    },
    handleClick: function(event) {
      event.target.classList.toggle('tz-location-search-filter__title--close');
      const isCollapsed =
        this.$refs.filterList[0].getAttribute('data-collapsed') === 'true';
      if (isCollapsed) {
        this.expandList();
        this.$refs.filterList[0].setAttribute('data-collapsed', 'false');
      } else {
        this.collapseList();
      }
    }
  }
};
</script>
